import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Check, Calendar, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/state/CartContext";
import CartButton from "@/components/CartButton";

const TICKETS = [
  // Confirmed shows — sorted by date.
  // Cities listed in `PRIVATE_TICKET_CITIES` in server.py are sold via
  // private invite-only links only, so they're absent from this storefront.
  { city: "sydney_2", label: "Sydney II", date: "Sun 27 Sep 2026 · 7pm", price: 100, hint: "AUD", currencySymbol: "$",
    venue: "Tatler Sydney" },
];

const PWYC_PRESETS = [10, 15, 20, 30];

/* ─── Chic line-icons ─── */
function VinylIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round">
      <circle cx="32" cy="32" r="28" />
      <circle cx="32" cy="32" r="23" />
      <circle cx="32" cy="32" r="18" />
      <circle cx="32" cy="32" r="13" />
      <circle cx="32" cy="32" r="7.5" fill="currentColor" opacity="0.92" />
      <circle cx="32" cy="32" r="1.4" fill="#050a1a" />
    </svg>
  );
}

function WavIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      {[
        [10, 32, 22], [14, 32, 14], [18, 32, 28], [22, 32, 10],
        [26, 32, 20], [30, 32, 30], [34, 32, 12], [38, 32, 24],
        [42, 32, 18], [46, 32, 28], [50, 32, 14], [54, 32, 20],
      ].map(([x, cy, h], i) => (
        <line key={i} x1={x} y1={cy - h / 2} x2={x} y2={cy + h / 2} />
      ))}
    </svg>
  );
}

function FilmIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="12" width="52" height="40" rx="1" />
      {/* sprocket holes */}
      {[14, 22, 30, 38, 46].map((cx) => (
        <rect key={`t${cx}`} x={cx - 1.5} y={15} width="3" height="3" />
      ))}
      {[14, 22, 30, 38, 46].map((cx) => (
        <rect key={`b${cx}`} x={cx - 1.5} y={46} width="3" height="3" />
      ))}
      {/* film cell */}
      <rect x="12" y="22" width="40" height="20" />
      <circle cx="32" cy="32" r="6" />
    </svg>
  );
}

function UphillIcon({ className = "" }) {
  // Minimal cassette tape — fits the early-2010s vintage of the 2012 track
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="16" width="48" height="32" rx="2" />
      <rect x="14" y="24" width="36" height="12" />
      <circle cx="22" cy="30" r="3" />
      <circle cx="42" cy="30" r="3" />
      <line x1="22" y1="42" x2="42" y2="42" />
      {/* upward arrow inside for "Uphill" */}
      <path d="M32 24 L36 30 M32 24 L28 30 M32 24 L32 34" strokeWidth="1.4" />
    </svg>
  );
}

/* Tour image used as the visual on each ticket card — matches the
 * /tickets cards + /shows/:city hero so the whole tour reads uniformly.
 */
const TOUR_CARD_IMAGE =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

function TourPosterIcon({ className = "" }) {
  return (
    <div
      className={`${className} bg-cover bg-center border border-white/15`}
      style={{ backgroundImage: `url(${TOUR_CARD_IMAGE})` }}
      aria-hidden
    />
  );
}

function MerchIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
      {/* simple t-shirt outline */}
      <path d="M16 16 L22 10 L32 14 L42 10 L48 16 L54 22 L48 28 L44 26 L44 54 L20 54 L20 26 L16 28 L10 22 Z" />
    </svg>
  );
}

/* ─── Section heading ─── */
function Section({ label, children, testid, subLink, subLinkPosition = "top" }) {
  const buttonEl = subLink ? (
    <Link
      to={subLink.to}
      className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 hover:border-[var(--mm-accent)] hover:bg-[rgba(184,148,63,0.08)] font-mono text-[11px] sm:text-xs uppercase tracking-[0.32em] text-white/85 hover:text-[var(--mm-accent)] transition"
      data-testid={subLink.testid}
    >
      {subLink.label}
    </Link>
  ) : null;

  return (
    <section className="w-full max-w-6xl mx-auto mb-14 sm:mb-20" data-testid={testid}>
      <div className="text-center mb-10 sm:mb-14">
        <h2
          className="deco-text text-3xl sm:text-5xl md:text-6xl leading-[0.95] uppercase"
          style={{ letterSpacing: "0.08em" }}
        >
          {label}
        </h2>
        <div className="mx-auto mt-4 h-px w-16 bg-[var(--mm-accent)]/40" aria-hidden />
        {buttonEl && subLinkPosition === "top" && (
          <div className="mt-7">{buttonEl}</div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 sm:gap-y-14">
        {children}
      </div>
      {buttonEl && subLinkPosition === "bottom" && (
        <div className="mt-10 sm:mt-14 text-center">{buttonEl}</div>
      )}
    </section>
  );
}

/* ─── Product shell — icon-led ─── */
function ProductFrame({ icon, title, subtitle, children, testid }) {
  return (
    <section className="product-row" data-testid={testid}>
      <header className="flex flex-col items-center text-center gap-3 mb-5">
        <div className="product-icon" aria-hidden>{icon}</div>
        <h3
          className="text-xl sm:text-2xl leading-tight"
          style={{ fontFamily: "'Italiana', serif", color: "#f3f2ee", letterSpacing: "0.04em" }}
          data-testid={`${testid}-title`}
        >
          {title}
        </h3>
        {subtitle && (
          <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.35em] text-white/55">
            {subtitle}
          </p>
        )}
      </header>
      {children}
    </section>
  );
}

/* ─── Pay-what-you-can (shared by WAV + Myth Film) ─── */
function PayWhatYouCan({ existing, onAdd, presets = PWYC_PRESETS, minAmount = 1, testid }) {
  const [amount, setAmount] = useState(existing?.custom_price ? String(existing.custom_price) : "");

  const submit = () => {
    const num = parseFloat(amount);
    if (!num || num < minAmount) return;
    onAdd(num);
  };

  return (
    <>
      {/* Custom "pay what you can" amount — prominent, above the presets */}
      <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/70 text-center mb-2 font-medium" data-testid={`${testid}-pwyc-label`}>
        Pay what you can
      </p>
      <div className="flex items-baseline justify-center gap-1.5 mb-5 border-b border-white/25 focus-within:border-[var(--mm-accent)] py-2 max-w-xs mx-auto">
        <span
          className="text-base leading-none text-white/55"
          style={{ fontFamily: "'Italiana', serif" }}
        >
          $
        </span>
        <input
          type="number"
          inputMode="decimal"
          min={minAmount}
          step="0.5"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="enter amount"
          className="pwyc-input bg-transparent outline-none text-lg text-center text-white w-32 leading-none placeholder:text-white/35 placeholder:italic"
          style={{ fontFamily: "'Italiana', serif" }}
          data-testid={`${testid}-amount`}
        />
        <span
          className="font-mono text-[10px] text-white/45 tracking-[0.32em] font-medium leading-none"
        >
          AUD
        </span>
      </div>

      <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/50 text-center mb-3 font-medium">
        Or choose
      </p>
      <div className="grid grid-cols-4 gap-1.5 mb-5" data-testid={`${testid}-presets`}>
        {presets.map((p) => {
          const active = parseFloat(amount) === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => setAmount(String(p))}
              className={`py-3 border text-base font-mono font-medium tracking-wide transition-all duration-300 ${
                active
                  ? "border-[var(--mm-accent)] text-[var(--mm-accent-hover)] bg-[rgba(184,148,63,0.10)]"
                  : "border-white/25 text-white/85 hover:border-white/60 hover:text-white"
              }`}
              data-testid={`${testid}-preset-${p}`}
            >
              ${p}
            </button>
          );
        })}
      </div>

      <button
        onClick={submit}
        disabled={!amount || parseFloat(amount) < minAmount}
        className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3"
        data-testid={`${testid}-add`}
      >
        {existing ? "Update price" : "Add to cart"} <Plus className="w-4 h-4" strokeWidth={1.4} />
      </button>
      {amount && parseFloat(amount) < minAmount && (
        <p className="font-mono text-[10px] text-rose-300/75 text-center mt-2 tracking-wide" data-testid={`${testid}-min-warning`}>
          Minimum ${minAmount} AUD
        </p>
      )}
    </>
  );
}

/* ─── WAV ─── */
function WavCard() {
  const { add, items, updateWavPrice } = useCart();
  const existing = items.find((i) => i.type === "wav");

  return (
    <ProductFrame
      icon={<WavIcon className="w-14 h-14 sm:w-16 sm:h-16" />}
      title="Metamorph · 9 Min Audio Package"
      subtitle="Lyrics · WAV quality audio"
      testid="product-wav"
    >
      <PayWhatYouCan
        existing={existing}
        onAdd={(price) => existing ? updateWavPrice(price) : add({ type: "wav", custom_price: price, quantity: 1 })}
        testid="wav"
      />
    </ProductFrame>
  );
}

/* ─── Myth Live Performance Film ─── */
function MythFilmCard() {
  const { add, items } = useCart();
  const existing = items.find((i) => i.type === "myth_film");

  return (
    <ProductFrame
      icon={<FilmIcon className="w-14 h-14 sm:w-16 sm:h-16" />}
      title="Myth · Live Performance Film"
      subtitle="Limited download"
      testid="product-myth-film"
    >
      <p className="text-center mb-5" data-testid="film-price">
        <span
          className="text-2xl text-[var(--mm-accent)]"
          style={{ fontFamily: "'Italiana', serif", letterSpacing: "0.04em" }}
        >
          $20
        </span>
        <span className="font-mono text-[10px] text-white/55 tracking-[0.28em] ml-2 font-medium">
          AUD
        </span>
      </p>
      <button
        onClick={() => add({ type: "myth_film", custom_price: 20, quantity: 1 })}
        disabled={!!existing}
        className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3"
        data-testid="film-add"
      >
        {existing ? "Added to cart" : (<>Add to cart <Plus className="w-4 h-4" strokeWidth={1.4} /></>)}
      </button>
    </ProductFrame>
  );
}

/* ─── Uphill 2012 MP3 — fixed-tier PWYC ($5/$10/$15/$20) ─── */
const UPHILL_TIERS = [5, 10, 15, 20];

function UphillCard() {
  const { add, items } = useCart();
  const existing = items.find((i) => i.type === "uphill");
  const [tier, setTier] = useState(5);

  return (
    <ProductFrame
      icon={<UphillIcon className="w-14 h-14 sm:w-16 sm:h-16" />}
      title="Uphill · 2012 MP3"
      subtitle="Inaugural George Maple release"
      testid="product-uphill"
    >
      <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/70 text-center mb-3 font-medium">
        Pay what you can
      </p>
      <div className="grid grid-cols-4 gap-1.5 mb-5" data-testid="uphill-tiers">
        {UPHILL_TIERS.map((t) => {
          const active = tier === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTier(t)}
              disabled={!!existing}
              className={`py-3 border text-base font-mono font-medium tracking-wide transition-all duration-300 ${
                active
                  ? "border-[var(--mm-accent)] text-[var(--mm-accent-hover)] bg-[rgba(184,148,63,0.10)]"
                  : "border-white/25 text-white/85 hover:border-white/60 hover:text-white"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`uphill-tier-${t}`}
            >
              ${t}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => add({ type: "uphill", custom_price: tier, quantity: 1 })}
        disabled={!!existing}
        className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3"
        data-testid="uphill-add"
      >
        {existing ? "Added to cart" : (<>Add to cart · ${tier} <Plus className="w-4 h-4" strokeWidth={1.4} /></>)}
      </button>
    </ProductFrame>
  );
}

/* ─── Qty stepper ─── */
function QtyStepper({ qty, setQty, max, disabled, testid }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <span className="font-mono text-xs uppercase tracking-[0.22em] text-white/70 font-medium">Qty</span>
      <div className="flex items-center border border-white/30" data-testid={testid}>
        <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1 || disabled} className="px-3 py-2 hover:bg-white/5 disabled:opacity-30" aria-label="Decrease">
          <Minus className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <span className="tabular-nums px-4 text-lg text-white font-medium font-mono">{qty}</span>
        <button onClick={() => setQty(Math.min(max ?? 10, qty + 1))} disabled={disabled || (max !== null && qty >= max)} className="px-3 py-2 hover:bg-white/5 disabled:opacity-30" aria-label="Increase">
          <Plus className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}

/* ─── Vinyl — on sale (add to cart) ─── */
function VinylCard({ soldOut = false }) {
  const { add, items, setQuantity, keyOf } = useCart();
  const existing = items.find((i) => i.type === "vinyl");
  const [qty, setQty] = useState(existing?.quantity || 1);

  // Keep local qty in sync if the cart drawer changes it from elsewhere.
  useEffect(() => { if (existing) setQty(existing.quantity || 1); }, [existing]);

  const onAdd = () => {
    if (existing) {
      // Reset the cart line to the picker's qty (rather than adding on top
      // of what's there) — feels more predictable on a re-click.
      setQuantity(keyOf({ type: "vinyl" }), qty);
    } else {
      add({ type: "vinyl", quantity: qty });
    }
  };

  const unit = 60;
  const bulk = qty >= 2;
  const base = unit * qty;
  const discount = bulk ? Math.round(base * 0.20) : 0;
  const total = base - discount;

  return (
    <ProductFrame
      icon={<VinylIcon className="w-14 h-14 sm:w-16 sm:h-16" />}
      title="Metamorph · Vinyl LP"
      subtitle="12&Prime; Vinyl"
      testid="product-vinyl"
    >
      <p className="text-center mb-2" data-testid="vinyl-price">
        <span
          className="text-2xl text-[var(--mm-accent)]"
          style={{ fontFamily: "'Italiana', serif", letterSpacing: "0.04em" }}
        >
          ${unit}
        </span>
        <span className="font-mono text-[10px] text-white/55 tracking-[0.28em] ml-2 font-medium">
          AUD
        </span>
      </p>
      <p
        className="font-mono text-[10px] text-[var(--mm-accent)]/90 mb-3 leading-relaxed text-center tracking-[0.24em] uppercase"
        data-testid="vinyl-bulk-hint"
      >
        20% off when you buy 2 or more
      </p>
      <p className="font-mono text-[10px] text-white/55 mb-4 leading-relaxed text-center tracking-[0.22em] uppercase">
        Estimated delivery &mdash; August 2026
      </p>

      {soldOut ? (
        <p
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-rose-300/80 text-center py-6"
          data-testid="vinyl-soldout"
        >
          Sold out
        </p>
      ) : (
        <>
          <QtyStepper qty={qty} setQty={setQty} max={5} testid="vinyl-qty" />

          {bulk && (
            <p
              className="font-mono text-[10px] text-[var(--mm-accent)] text-center tracking-[0.18em] mb-3"
              data-testid="vinyl-bulk-applied"
            >
              −20% bulk discount applied &middot; ${total} AUD
            </p>
          )}

          <button
            onClick={onAdd}
            className="btn-line w-full justify-center text-sm py-3"
            data-testid="vinyl-add"
          >
            {existing
              ? (qty === existing.quantity
                  ? <>Added to cart <Check className="w-4 h-4" strokeWidth={1.4} /></>
                  : <>Update quantity <Plus className="w-4 h-4" strokeWidth={1.4} /></>)
              : <>Add to cart <Plus className="w-4 h-4" strokeWidth={1.4} /></>}
          </button>

          <p className="font-mono text-[10px] text-white/45 text-center mt-3 leading-relaxed tracking-wide">
            Shipping calculated at checkout · AU/UK $15 · EU/US/Canada/RoW $20
          </p>
        </>
      )}
    </ProductFrame>
  );
}

/* ─── Ticket ─── */
/* ─── Coming-soon city register-interest card ─── */
function ComingSoonTicketCard({ ticket }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/tickets/interest", {
        city: ticket.city,
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <ProductFrame
      icon={<TourPosterIcon className="w-14 h-16 sm:w-16 sm:h-20" />}
      title={`${ticket.label} Tickets`}
      subtitle="Register your interest"
      testid={`product-ticket-${ticket.city}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55 mb-4 text-center">
        Register your interest
      </p>
      {status === "done" ? (
        <div className="flex flex-col items-center gap-2 py-4" data-testid={`ticket-interest-${ticket.city}-done`}>
          <Check className="w-5 h-5 text-[var(--mm-accent-hover)]" strokeWidth={1.4} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">Noted.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2.5" data-testid={`ticket-interest-${ticket.city}-form`}>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45"
            data-testid={`ticket-interest-${ticket.city}-name`}
          />
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45"
            data-testid={`ticket-interest-${ticket.city}-email`}
            required
          />
          <button
            type="submit"
            disabled={status === "submitting" || !email.trim()}
            className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed mt-2"
            data-testid={`ticket-interest-${ticket.city}-submit`}
          >
            {status === "submitting" ? "Sending…" : "Notify me"}
          </button>
          {status === "error" && <p className="font-mono text-[10px] text-red-400/75 text-center mt-1">Something went wrong — try again.</p>}
        </form>
      )}
    </ProductFrame>
  );
}

function TicketCard({ ticket, soldOut }) {
  const { add } = useCart();
  const [qty, setQty] = useState(1);

  // "Coming soon" cities — render an inline register-interest form instead of
  // the buy flow. Interest captures go to /api/tickets/interest.
  if (ticket.comingSoon) {
    return <ComingSoonTicketCard ticket={ticket} />;
  }

  const isSoldOut = !!soldOut;

  return (
    <ProductFrame
      icon={<TourPosterIcon className="w-14 h-16 sm:w-16 sm:h-20" />}
      title={`${ticket.label} Tickets`}
      subtitle=""
      testid={`product-ticket-${ticket.city}`}
    >
      <p className="flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-white/70 mb-1.5" data-testid={`ticket-date-${ticket.city}`}>
        <Calendar className="w-3 h-3" strokeWidth={1.4} /> {ticket.date}
      </p>
      {ticket.venue && (
        <p className="flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-white/55 mb-4 leading-relaxed text-center" data-testid={`ticket-venue-${ticket.city}`}>
          <MapPin className="w-3 h-3" strokeWidth={1.4} /> {ticket.venue}
        </p>
      )}
      <div className="flex items-baseline justify-between mb-4">
        <p className="font-mono text-base uppercase tracking-[0.2em] text-white/90 font-medium" data-testid={`ticket-price-${ticket.city}`}>
          {ticket.currencySymbol || "$"}{ticket.price} <span className="opacity-65 text-xs">{ticket.hint}</span>
        </p>
        {isSoldOut && (
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-rose-300/85 font-medium" data-testid={`ticket-soldout-${ticket.city}`}>
            Sold out
          </p>
        )}
      </div>

      <QtyStepper qty={qty} setQty={setQty} max={10} disabled={isSoldOut} testid={`ticket-qty-${ticket.city}`} />

      <button onClick={() => add({ type: "ticket", city: ticket.city, quantity: qty })} disabled={isSoldOut} className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3" data-testid={`ticket-add-${ticket.city}`}>
        {isSoldOut ? "Sold out" : (<>Add to cart <Plus className="w-4 h-4" strokeWidth={1.4} /></>)}
      </button>
    </ProductFrame>
  );
}

/* ─── Tour interest form ─── */
function TourInterestCard() {
  const [city, setCity] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!city.trim() || !email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/tickets/interest", {
        city: city.trim(),
        email: email.trim(),
        name: name.trim() || undefined,
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <ProductFrame
      icon={<TourPosterIcon className="w-14 h-16 sm:w-16 sm:h-20" />}
      title="Tour in your city?"
      subtitle="Register interest"
      testid="product-tour-interest"
    >
      <p className="font-serif italic text-[11px] sm:text-xs text-white/55 mb-4 leading-snug text-center">
        Tell us where to take Metamorph next.
      </p>
      {status === "done" ? (
        <div className="flex flex-col items-center gap-2 py-4" data-testid="tour-interest-done">
          <Check className="w-5 h-5 text-[var(--mm-accent-hover)]" strokeWidth={1.4} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">Noted.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2.5" data-testid="tour-interest-form">
          <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Your city" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="tour-interest-city" required />
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="tour-interest-name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="tour-interest-email" required />
          <button type="submit" disabled={status === "submitting" || !city.trim() || !email.trim()} className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed mt-2" data-testid="tour-interest-submit">
            {status === "submitting" ? "Sending…" : "Register interest"}
          </button>
          {status === "error" && <p className="font-mono text-[10px] text-red-400/75 text-center mt-1">Something went wrong — try again.</p>}
        </form>
      )}
    </ProductFrame>
  );
}

/* ─── Merch interest form ─── */
function MerchInterestCard() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("submitting");
    try {
      await api.post("/merch/interest", {
        email: email.trim(),
        name: name.trim() || undefined,
        note: note.trim() || undefined,
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <ProductFrame
      icon={<MerchIcon className="w-14 h-14 sm:w-16 sm:h-16" />}
      title="Merchandise?"
      subtitle="Register interest"
      testid="product-merch-interest"
    >
      <p className="font-serif italic text-[11px] sm:text-xs text-white/55 mb-4 leading-snug text-center">
        Coming soon — let us know if you&apos;d like a piece.
      </p>
      {status === "done" ? (
        <div className="flex flex-col items-center gap-2 py-4" data-testid="merch-interest-done">
          <Check className="w-5 h-5 text-[var(--mm-accent-hover)]" strokeWidth={1.4} />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">Noted.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-2.5" data-testid="merch-interest-form">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="merch-interest-name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="merch-interest-email" required />
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What would you like? (optional)" className="bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-3 py-3 text-base text-white font-mono tracking-wide placeholder-white/45" data-testid="merch-interest-note" />
          <button type="submit" disabled={status === "submitting" || !email.trim()} className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed mt-2" data-testid="merch-interest-submit">
            {status === "submitting" ? "Sending…" : "Register interest"}
          </button>
          {status === "error" && <p className="font-mono text-[10px] text-red-400/75 text-center mt-1">Something went wrong — try again.</p>}
        </form>
      )}
    </ProductFrame>
  );
}

export default function Store() {
  const [availability, setAvailability] = useState(null);
  const { open: cartOpen, items } = useCart();

  const fetchAvailability = () => {
    api.get("/store/availability").then((r) => setAvailability(r.data)).catch(() => {});
  };

  useEffect(() => { fetchAvailability(); }, []);
  useEffect(() => { if (cartOpen) fetchAvailability(); }, [cartOpen]);
  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  useEffect(() => {
    const id = setInterval(fetchAvailability, 60000);
    return () => clearInterval(id);
  }, []);

  const soldOutFor = (city) => Boolean(availability?.cities?.[city]?.sold_out);
  const vinylSoldOut = Boolean(availability?.vinyl?.sold_out);

  return (
    <main
      className="relative min-h-[100svh] w-full text-white overflow-hidden flex flex-col px-4 sm:px-10 py-5 sm:py-8"
      data-testid="store-page"
    >
      <div className="film-grain" aria-hidden />

      <header className="relative z-10 flex items-center justify-between mb-8 sm:mb-12">
        <Link to="/manifesto" className="vintage-link text-[10px] sm:text-xs uppercase" data-testid="store-back">
          ← Manifesto
        </Link>
        <CartButton />
      </header>

      <div className="relative z-10 text-center mb-10 sm:mb-12 mm-fade-in">
        <h1 className="deco-text text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-[0.04em] text-center uppercase" data-testid="store-title">
          Metamorph
        </h1>
        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.4em] mt-3 opacity-70" data-testid="store-byline">
          by George Maple
        </p>
      </div>

      {/* Note from the artist */}
      <div className="relative z-10 max-w-xl mx-auto text-center mb-12 sm:mb-16 px-4 mm-fade-in d1" data-testid="store-artist-note">
        <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.4em] opacity-85 mb-4">
          A note from George
        </p>
        <p className="font-serif italic text-sm sm:text-base text-white/75 leading-relaxed">
          Thank you endlessly for your time, presence and care.
          It is deeply seen and appreciated.
          I hope you enjoy the offerings.
        </p>
        <p className="font-serif italic text-sm sm:text-base text-[var(--mm-accent)] mt-3">
          GM&nbsp;x
        </p>
      </div>

      <div className="relative z-10 w-full mm-fade-in d1">
        <Section
          label="Music"
          testid="section-music"
        >
          <WavCard />
          <VinylCard soldOut={vinylSoldOut} />
          <MythFilmCard />
          <UphillCard />
        </Section>

        <Section
          label="Live"
          testid="section-live"
          subLink={{ to: "/tour", label: "About Metamorph live →", testid: "store-live-discover" }}
        >
          {TICKETS.map((t) => (
            <TicketCard key={t.city} ticket={t} soldOut={soldOutFor(t.city)} />
          ))}
          <TourInterestCard />
        </Section>

        <Section label="Merchandise" testid="section-merch">
          <MerchInterestCard />
        </Section>
      </div>

      <footer className="relative z-10 mt-6 pb-2 flex flex-col items-center gap-4">
      </footer>

      {/* Floating contact — fixed bottom-left, clear of Emergent watermark */}
      <Link
        to="/contact"
        className="vintage-link fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-30 text-[10px] sm:text-xs uppercase"
        data-testid="store-contact"
      >
        Contact
      </Link>
    </main>
  );
}
